import {
  Connection,
  Transaction,
  Keypair,
  Signer,
  PublicKey,
} from '@solana/web3.js';
import { KSMProvider } from '../ksm';

export interface ConfirmTransactionOptions {
  maxRetries?: number;
  commitment?: 'processed' | 'confirmed' | 'finalized';
  initialDelay?: number;
  maxDelay?: number;
}

export interface SendTransactionOptions extends ConfirmTransactionOptions {
  skipPreflight?: boolean;
  preflightCommitment?: 'processed' | 'confirmed' | 'finalized';
  pollInterval?: number;
  timeoutMs?: number;
  useKSM?: boolean;
  ksmProvider?: KSMProvider;
  feePayer?: PublicKey;
}

/**
 * Confirms a Solana transaction with retry logic and exponential backoff
 *
 * @param connection - Solana connection instance
 * @param signature - Transaction signature to confirm
 * @param options - Configuration options for retry behavior
 * @returns Promise that resolves when transaction is confirmed
 * @throws Error if transaction fails to confirm after all retries
 */
export async function confirmTransactionWithRetry(
  connection: Connection,
  signature: string,
  options: ConfirmTransactionOptions = {},
): Promise<void> {
  const {
    maxRetries = 3,
    commitment = 'confirmed',
    initialDelay = 2000,
    maxDelay = 30000,
  } = options;

  for (let attempt = 1; attempt <= maxRetries; attempt++) {
    try {
      // Use getSignatureStatus instead of confirmTransaction for better reliability
      const result = await connection.getSignatureStatus(signature);

      if (
        result?.value?.confirmationStatus === commitment ||
        result?.value?.confirmationStatus === 'finalized'
      ) {
        return; // Success
      }

      if (result?.value?.err) {
        throw new Error(
          `Transaction failed: ${JSON.stringify(result.value.err)}`,
        );
      }

      // If not confirmed yet, wait and retry
      if (attempt < maxRetries) {
        const delay = Math.min(
          initialDelay * Math.pow(2, attempt - 1),
          maxDelay,
        );
        await new Promise((resolve) => setTimeout(resolve, delay));
      }
    } catch (error) {
      console.warn(
        `Confirmation attempt ${attempt}/${maxRetries} failed:`,
        error,
      );

      if (attempt === maxRetries) {
        throw new Error(`Transaction confirmation failed: ${error}`);
      }

      const delay = Math.min(initialDelay * Math.pow(2, attempt - 1), maxDelay);
      await new Promise((resolve) => setTimeout(resolve, delay));
    }
  }
}

/**
 * Alternative confirmation method using getSignatureStatus polling
 * Useful when confirmTransaction timeouts are too restrictive
 *
 * @param connection - Solana connection instance
 * @param signature - Transaction signature to confirm
 * @param options - Configuration options
 * @returns Promise that resolves when transaction is confirmed
 */
export async function confirmTransactionWithPolling(
  connection: Connection,
  signature: string,
  options: ConfirmTransactionOptions & {
    pollInterval?: number;
    timeoutMs?: number;
  } = {},
): Promise<void> {
  const {
    commitment = 'confirmed',
    pollInterval = 2000,
    timeoutMs = 60000,
  } = options;

  const startTime = Date.now();

  while (Date.now() - startTime < timeoutMs) {
    try {
      const result = await connection.getSignatureStatus(signature);

      if (
        result?.value?.confirmationStatus === commitment ||
        (commitment === 'confirmed' &&
          result?.value?.confirmationStatus === 'finalized')
      ) {
        return; // Transaction confirmed
      }

      if (result?.value?.err) {
        throw new Error(
          `Transaction failed: ${JSON.stringify(result.value.err)}`,
        );
      }

      // Wait before next poll
      await new Promise((resolve) => setTimeout(resolve, pollInterval));
    } catch (error) {
      if (Date.now() - startTime >= timeoutMs) {
        throw new Error(
          `Transaction confirmation timed out after ${timeoutMs}ms: ${error}`,
        );
      }

      // Continue polling on network errors
      await new Promise((resolve) => setTimeout(resolve, pollInterval));
    }
  }

  throw new Error(`Transaction confirmation timed out after ${timeoutMs}ms`);
}

/**
 * Robust transaction sending with full retry logic
 * Handles fresh blockhashes, expiration detection, and complete transaction retry
 *
 * @param connection - Solana connection instance
 * @param transaction - Transaction to send
 * @param signers - Array of signers for the transaction
 * @param options - Configuration options
 * @returns Promise that resolves to transaction signature
 */
export async function sendTransactionWithRetry(
  connection: Connection,
  transaction: Transaction,
  signers: (Keypair | Signer)[],
  options: SendTransactionOptions = {},
): Promise<string> {
  const {
    maxRetries = 3,
    commitment = 'confirmed',
    skipPreflight = false,
    preflightCommitment = 'confirmed',
    initialDelay = 2000,
    maxDelay = 30000,
    useKSM = false,
    ksmProvider,
    feePayer,
  } = options;

  for (let attempt = 1; attempt <= maxRetries; attempt++) {
    try {
      // Get fresh blockhash for each attempt
      const { blockhash, lastValidBlockHeight } =
        await connection.getLatestBlockhash(commitment);
      transaction.recentBlockhash = blockhash;
      transaction.lastValidBlockHeight = lastValidBlockHeight;

      let signature = '';

      if (useKSM) {
        if (!feePayer) {
          console.error('Fee payer is required when using KSM');
          throw new Error('Fee payer is required when using KSM');
        }

        if (!ksmProvider) {
          console.error('KSM provider is required when using KSM');
          throw new Error('KSM provider is required when using KSM');
        }

        transaction.feePayer = feePayer;

        const ksmSignedTransaction =
          await ksmProvider.signTransaction(transaction);

        ksmSignedTransaction.sign([...signers]);

        signature = await connection.sendRawTransaction(
          ksmSignedTransaction.serialize(),
          {
            skipPreflight,
            preflightCommitment,
          },
        );
      } else {
        transaction.feePayer = signers[signers.length - 1].publicKey;
        // Sign transaction
        transaction.sign(...signers);

        // Send transaction
        signature = await connection.sendRawTransaction(
          transaction.serialize(),
          {
            skipPreflight,
            preflightCommitment,
          },
        );
      }

      console.log(`Transaction sent (attempt ${attempt}): ${signature}`);

      // Wait for confirmation with blockhash validation
      await waitForTransactionConfirmation(
        connection,
        signature,
        lastValidBlockHeight,
        { commitment, timeoutMs: 60000 },
      );

      console.log(`Transaction confirmed: ${signature}`);
      return signature;
    } catch (error) {
      console.warn(
        `Transaction attempt ${attempt}/${maxRetries} failed:`,
        error,
      );

      if (attempt === maxRetries) {
        throw new Error(
          `Transaction failed after ${maxRetries} attempts: ${error instanceof Error ? error.message : String(error)}`,
        );
      }

      // Wait before retry with exponential backoff
      const delay = Math.min(initialDelay * Math.pow(2, attempt - 1), maxDelay);
      console.info(`Retrying transaction in ${delay}ms...`);
      await new Promise((resolve) => setTimeout(resolve, delay));
    }
  }

  throw new Error('Transaction failed after all retry attempts');
}

/**
 * Advanced confirmation method with blockhash expiration detection
 *
 * @param connection - Solana connection instance
 * @param signature - Transaction signature to confirm
 * @param lastValidBlockHeight - Block height when transaction expires
 * @param options - Configuration options
 * @returns Promise that resolves when transaction is confirmed
 */
export async function waitForTransactionConfirmation(
  connection: Connection,
  signature: string,
  lastValidBlockHeight: number,
  options: {
    commitment?: 'processed' | 'confirmed' | 'finalized';
    timeoutMs?: number;
    pollInterval?: number;
  } = {},
): Promise<void> {
  const {
    commitment = 'confirmed',
    timeoutMs = 60000,
    pollInterval = 2000,
  } = options;

  const startTime = Date.now();

  while (Date.now() - startTime < timeoutMs) {
    try {
      // Check if blockhash is still valid
      const currentBlockHeight = await connection.getBlockHeight();
      if (currentBlockHeight > lastValidBlockHeight) {
        throw new Error('Transaction expired: blockhash no longer valid');
      }

      // Poll for transaction status
      const status = await connection.getSignatureStatus(signature);

      if (status?.value?.err) {
        throw new Error(
          `Transaction failed: ${JSON.stringify(status.value.err)}`,
        );
      }

      if (
        status?.value?.confirmationStatus === commitment ||
        (commitment === 'confirmed' &&
          status?.value?.confirmationStatus === 'finalized')
      ) {
        console.log(
          `Transaction confirmed with status: ${status.value.confirmationStatus}`,
        );
        return;
      }

      // Wait before next poll
      await new Promise((resolve) => setTimeout(resolve, pollInterval));
    } catch (error) {
      if (Date.now() - startTime >= timeoutMs) {
        throw new Error(
          `Transaction confirmation timed out after ${timeoutMs}ms: ${error}`,
        );
      }

      // Continue polling on network errors unless it's a critical error
      if (
        error instanceof Error &&
        (error.message.includes('expired') || error.message.includes('failed'))
      ) {
        throw error;
      }

      await new Promise((resolve) => setTimeout(resolve, pollInterval));
    }
  }

  throw new Error(`Transaction confirmation timed out after ${timeoutMs}ms`);
}

/**
 * Convenience method for oracle operations - sends transaction with maximum reliability
 *
 * @param connection - Solana connection instance
 * @param transaction - Transaction to send
 * @param signers - Array of signers for the transaction
 * @returns Promise that resolves to transaction signature
 */
export async function sendOracleTransaction(
  connection: Connection,
  transaction: Transaction,
  signers: (Keypair | Signer)[],
): Promise<string> {
  return sendTransactionWithRetry(connection, transaction, signers, {
    maxRetries: 5, // More retries for critical oracle operations
    commitment: 'confirmed',
    skipPreflight: false, // Enable preflight for error detection
    preflightCommitment: 'confirmed',
    initialDelay: 1000,
    maxDelay: 30000,
  });
}

/**
 * Method for sending a transaction and signing it with KSM
 *
 * @param connection - Solana connection instance
 * @param transaction - Transaction to send
 * @param signers - Array of signers for the transaction except for the last one which is the KSM signer
 * @returns Promise that resolves to transaction signature
 */
export async function sendTransactionAndSignWithKSM(
  connection: Connection,
  transaction: Transaction,
  signers: (Keypair | Signer)[],
  ksmProvider: KSMProvider,
  feePayer: PublicKey,
  maxRetries = 2,
) {
  return sendTransactionWithRetry(connection, transaction, signers, {
    maxRetries, // More retries for critical oracle operations
    commitment: 'confirmed',
    skipPreflight: false, // Enable preflight for error detection
    preflightCommitment: 'confirmed',
    initialDelay: 1000,
    maxDelay: 30000,
    useKSM: true,
    ksmProvider,
    feePayer,
  });
}
